import { Alert, Modal } from "@fluidattacks/design";
import { isEmpty } from "lodash";
import { Fragment, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IRemoveCredentialsModal } from "./types.ts";

const RemoveCredentials = ({
  onRemove,
  selectedCredentials,
  modalRef,
}: Readonly<IRemoveCredentialsModal>): JSX.Element => {
  const prefix = "organization.tabs.credentials.actionButtons.";
  const { t } = useTranslation();
  const usedBy = useMemo((): string[] => {
    if (
      selectedCredentials === undefined ||
      isEmpty(selectedCredentials.usedBy)
    ) {
      return [];
    }

    return selectedCredentials.usedBy
      .filter((group): boolean => group !== undefined)
      .map(String);
  }, [selectedCredentials]);
  const canConfirm = useMemo((): boolean => usedBy.length === 0, [usedBy]);

  const confirmButton = t("buttons.confirm");
  const cancelButton = t("organization.tabs.users.removeButton.cancel");

  const onClose = useCallback((): void => {
    modalRef.close();
  }, [modalRef]);

  return (
    <Fragment>
      <Modal
        cancelButton={{
          onClick: onClose,
          text: cancelButton,
        }}
        confirmButton={
          canConfirm
            ? {
                onClick: onRemove,
                text: confirmButton,
              }
            : undefined
        }
        description={
          canConfirm ? (
            t(`${prefix}removeButton.confirmMessage`, {
              credentialName: selectedCredentials?.name,
            })
          ) : (
            <Fragment>
              <Alert variant={"warning"}>
                {t(`${prefix}removeButton.errorWarning`)}
              </Alert>
              <br />
              {t(`${prefix}removeButton.error`, { roots: usedBy.join(", ") })}
            </Fragment>
          )
        }
        modalRef={modalRef}
        size={"md"}
        title={t(`${prefix}removeButton.confirmTitle`)}
      />
    </Fragment>
  );
};

export { RemoveCredentials };
